import React, { useEffect, useState, useRef } from 'react';
import { CartaoModal, VerificarAcessoModal } from '../Modais/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEye, 
    faStar, 
    faCopy, 
    faChevronLeft, 
    faDownload,
    faLocationArrow
} from '@fortawesome/free-solid-svg-icons';
import checklistGif from '../../assets/img/checklist.gif';
import { validarOTP } from '../ValidarForm/ValidarForm';

const Favoritos = ({ 
    grupos, 
    handleBackToCards, 
    handleDownload,
    getCookie,
    showToast,
    navigateToGroup
}) => {
    const [favoritos, setFavoritos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(10);
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [passwordToShow, setPasswordToShow] = useState('');
    const [loadingSenha, setLoadingSenha] = useState(false);
    const [showCartaoModal, setShowCartaoModal] = useState(false);
    const [cartaoSelecionado, setCartaoSelecionado] = useState(null);
    const [showCopySuccess, setShowCopySuccess] = useState(false);
    const [showVerificarAcessoModal, setShowVerificarAcessoModal] = useState(false);
    const [registroSelecionado, setRegistroSelecionado] = useState(null);
    const [acaoPendente, setAcaoPendente] = useState(null);
    const [acessoVerificado, _setAcessoVerificado] = useState(false);
    const acessoVerificadoRef = useRef(false);

    const setAcessoVerificado = (value) => {
        acessoVerificadoRef.current = value;
        _setAcessoVerificado(value);
    };

    // Função para buscar os favoritos
    const fetchFavoritos = async () => {
        setLoading(true);
        try {
            const token_autorizado = getCookie("info");
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/registros/buscar_favoritos`, {
                method: 'POST',
                headers: {
                    'Authority': token_autorizado,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            });
            
            const result = await response.json();
            if (result.status === "ok") {
                setFavoritos(result.lista);
            } else {
                showToast(result.mensagem || 'Erro ao carregar favoritos', 'error');
            }
        } catch (error) {
            console.error('Erro ao buscar favoritos:', error);
            showToast('Erro ao carregar favoritos', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Função para remover favorito
    const handleRemoveFavorito = async (registro) => {
        try {
            const token_autorizado = getCookie("info");
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/registros/registro_favorito`, {
                method: 'POST',
                headers: {
                    'Authority': token_autorizado,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    opcao: "desfavoritar",
                    id: registro._id
                }),
            });

            const result = await response.json();
            if (result.status === "ok") {
                showToast('Registro removido dos favoritos', 'success');
                fetchFavoritos(); // Recarrega a lista
            } else {
                showToast(result.mensagem || 'Erro ao remover favorito', 'error');
            }
        } catch (error) {
            console.error('Erro ao remover favorito:', error);
            showToast('Erro ao remover favorito', 'error');
        }
    };

    // Função para formatar número de cartão
    const formatCardNumber = (cardNumber) => {
        if (!cardNumber) return '•••• •••• •••• ••••';
        const digitsOnly = cardNumber.toString().replace(/\D/g, '');
        const lastFour = digitsOnly.slice(-4);
        return `•••• •••• •••• ${lastFour}`;
    };

    // Paginação
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = favoritos.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(favoritos.length / recordsPerPage);

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const fetchSenhaDescriptografada = async (registroId, isCartao = false) => {
        setLoadingSenha(true);
        try {
            const token_autorizado = getCookie("info");
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/registros/verChave?id=${registroId}&opcao=${isCartao ? 'visualizar_cartao' : 'visualizar_senha'}`, {
                headers: {
                    'Authority': token_autorizado,
                },
            });
    
            if (!response.ok) {
                throw new Error(`Erro HTTP: ${response.status}`);
            }
    
            const result = await response.json();
            
            if (result.status === "ok") {
                if (isCartao) {
                    setCartaoSelecionado(result.registro);
                    setShowCartaoModal(true);
                    setAcessoVerificado(false);
                } else {
                    return result.chave;
                }
            } else if (result.status === "bloqueado") {
                setShowVerificarAcessoModal(true);
            } else {
                throw new Error(result.mensagem || 'Erro no servidor');
            }
        } catch (error) {
            console.error('Erro ao buscar senha:', error);
            showToast(error.message || 'Erro ao visualizar senha', 'error');
            return null;
        } finally {
            setLoadingSenha(false);
        }
    };

    const handleShowOrCopyPassword = async (registro, copiar = false) => {
        const isCartao = registro.opcao_sistema === 'cartao';

        // Se for cartão e ainda não verificou o acesso, exibe o modal e aguarda
        if (isCartao && !acessoVerificadoRef.current) {
            setRegistroSelecionado(registro);
            setAcaoPendente(() => () => handleShowOrCopyPassword(registro, copiar));
            setShowVerificarAcessoModal(true);
            return;
        }

        if (copiar) {
            if (isCartao) {
                showToast('Não é possível copiar dados de cartão', 'warning');
                return;
            }

            setLoadingSenha(true);
            try {
                const senhaDescriptografada = await fetchSenhaDescriptografada(registro._id);
                if (senhaDescriptografada) {
                    navigator.clipboard.writeText(senhaDescriptografada).then(() => {
                        setShowCopySuccess(true);
                        setShowPasswordPopup(false);
                        setTimeout(() => setShowCopySuccess(false), 3000);
                    });
                }
            } finally {
                setLoadingSenha(false);
            }
        } else {
            setRegistroSelecionado(registro);
            if (isCartao) {
                await fetchSenhaDescriptografada(registro._id, true);
                setShowCartaoModal(true);
            } else {
                const senhaDescriptografada = await fetchSenhaDescriptografada(registro._id);
                if (senhaDescriptografada) {
                    setPasswordToShow(senhaDescriptografada);
                    setShowPasswordPopup(true);
                }
            }
        }
    };

    const verificarCodigo = (codigo, fecharModal, setIsLoading) => {
        const registro = registroSelecionado;
        
        validarOTP(
            codigo,
            "validacao",
            async () => {
                fecharModal();
                setAcessoVerificado(true);
                setIsLoading(false);
                
                if (acaoPendente) {
                    await acaoPendente();
                    setAcaoPendente(null);
                }
            },
            () => {},
            setIsLoading
        );
    };

    const cancelarVerificacao = () => {
        setShowVerificarAcessoModal(false);
        setRegistroSelecionado(null);
        setAcaoPendente(null);
        setAcessoVerificado(false);
    };

    // Busca os favoritos quando o componente é montado
    useEffect(() => {
        fetchFavoritos();
    }, []);

    if (loading) {
        return (
            <section className="group-details">
                <div className="header-right">
                    <button className="btn-back" onClick={handleBackToCards}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <div className='header-fav'> 
                        <h2>Favoritos</h2>
                    </div>
                </div>
                <div className="loading">Carregando favoritos...</div>
            </section>
        );
    }

    return (
        <>
            <section className="group-details">
                {showCopySuccess && (
                    <div className="password-popup-overlay">
                        <div className="password-popup">
                            <div className="password-popup-content">
                                <img src={checklistGif} alt="Confirmação" style={{ width: '80px', height: '80px' }} />
                                <p>Senha copiada com sucesso!</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="header-right">
                    <button className="btn-back" onClick={handleBackToCards}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <div className='header-fav'>
                        <h2>Favoritos</h2>
                    </div>
                </div>
                
                <div className="card-details">
                    <h3>Registros Favoritos:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th></th>
                                <th>TÍTULO</th>
                                <th>LOGIN</th>
                                <th>SISTEMA</th>
                                <th>NOTAS</th>
                                <th>OPÇÕES</th>
                                <th>ARQUIVO</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentRecords.length > 0 ? (
                                currentRecords.map((registro, index) => (
                                    <tr key={registro._id || index}>
                                        <td>
                                            <FontAwesomeIcon
                                                icon={faStar}
                                                className="icon favorito"
                                                style={{ color: '#FFD700', cursor: 'pointer' }}
                                                onClick={() => handleRemoveFavorito(registro)}
                                                title="Remover dos favoritos"
                                            />
                                        </td>
                                        <td>{registro.titulo}</td>
                                        <td>
                                            {registro.opcao_sistema === 'cartao' ? (
                                                <span className="card-number-masked">
                                                    {formatCardNumber(registro.numero || '')}
                                                </span>
                                            ) : (
                                                registro.login || ''
                                            )}
                                        </td>
                                        <td>
                                            {registro.opcao_sistema === 'cartao' ? (
                                                <span style={{ textTransform: 'capitalize' }}>
                                                    {registro.sistema?.toLowerCase() === 'mastercard' ? 'Mastercard' :
                                                    registro.sistema?.toLowerCase() === 'visa' ? 'Visa' :
                                                    registro.sistema?.toLowerCase() === 'elo' ? 'Elo' : 
                                                    registro.sistema || 'Outro'}
                                                </span>
                                            ) : (
                                                registro.sistema || ''
                                            )}
                                        </td>
                                        <td>{registro.notas || ''}</td>
                                        <td>
                                            <div className="icon-group">
                                                {registro.tem_senha === true && (
                                                    <button
                                                        className="mini-button"
                                                        onClick={() => handleShowOrCopyPassword(registro)}
                                                        title={loadingSenha ? "Carregando..." : "Visualizar senha"}
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </button>
                                                )}
                                                {registro.opcao_sistema !== 'cartao' && registro.tem_senha === true && (
                                                    <button
                                                        className="mini-button"
                                                        onClick={() => handleShowOrCopyPassword(registro, true)}
                                                        title={loadingSenha ? "Carregando..." : "Copiar senha"}
                                                    >
                                                        <FontAwesomeIcon icon={faCopy} />
                                                    </button>
                                                )}
                                                <button
                                                    className="mini-button"
                                                    onClick={() => navigateToGroup(registro.grupos)}
                                                    title={`Voltar para o grupo de origem`}
                                                >
                                                    <FontAwesomeIcon icon={faLocationArrow} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            {registro.nome_arquivo && (
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span 
                                                        className="arquivo-nome"
                                                        onClick={() => handleDownload(registro)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {registro.nome_arquivo}
                                                    </span>
                                                    <FontAwesomeIcon 
                                                        icon={faDownload} 
                                                        className="icon" 
                                                        onClick={() => handleDownload(registro)}
                                                        style={{ marginLeft: '8px', cursor: 'pointer' }}
                                                        title="Download do arquivo"
                                                    />
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7">Nenhum registro favoritado</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    
                    {favoritos.length > 0 && (
                        <div className="pagination">
                            <button onClick={prevPage} disabled={currentPage === 1}>◀</button>
                            <span> {currentPage} de {totalPages}</span>
                            <button onClick={nextPage} disabled={currentPage === totalPages}>▶</button>
                            <div className="total"><p style={{ color: 'gray' }}><strong>Total: </strong>{favoritos.length}</p></div>
                        </div>
                    )}
                </div>
            </section>

            {showPasswordPopup && (
                <>
                    <div className="password-popup-overlay"></div>
                    <div className='password-popup'>
                        <div className='password-timer-bar' /> 
                        <div className='password-popup-content'>
                            <p>{passwordToShow}</p>
                            <div className='password-popup-buttons'>
                                <button
                                    className='btn-copy'
                                    onClick={() => handleShowOrCopyPassword(registroSelecionado, true)}
                                >
                                    Copiar
                                </button>
                                <button
                                    className='btn-close'
                                    onClick={() => setShowPasswordPopup(false)}
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <CartaoModal 
                show={showCartaoModal}
                fecharModal={() => setShowCartaoModal(false)}
                cartao={cartaoSelecionado}
            />

            <VerificarAcessoModal
                show={showVerificarAcessoModal}
                fecharModal={() => setShowVerificarAcessoModal(false)} 
                cancelarVerificacao={cancelarVerificacao}
                verificarCodigo={verificarCodigo}
                showVerificarAcessoModal={showVerificarAcessoModal}
                registro={registroSelecionado}
                validarOTP={validarOTP}
                getCookie={getCookie}
                showToast={showToast}
            />
        </>
    );
};

export default Favoritos;